import { useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";

const nameOf = (s) => (s.name && s.name !== "—" ? s.name : s.subject.slice(0, 10));

/** Compact, searchable subject selector for the console header. */
export const SubjectPicker = ({ subjects, subject, onPick }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const box = useRef(null);

  const current = subjects.find((s) => s.subject === subject);
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return subjects;
    return subjects.filter(
      (s) => nameOf(s).toLowerCase().includes(needle) || s.subject.toLowerCase().includes(needle)
    );
  }, [subjects, q]);

  return (
    <div
      ref={box}
      data-testid="subject-switcher"
      className="relative"
      onBlur={(e) => {
        if (!box.current?.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <button
        data-testid="subject-picker-trigger"
        onClick={() => setOpen((v) => !v)}
        className="flex min-w-[15rem] items-center justify-between gap-4 border border-[var(--line-hi)] bg-[#0a0a0d] px-4 py-2.5 text-left transition-colors hover:border-zinc-500"
      >
        <span className="min-w-0">
          <span className="label block !text-[9px]">Subject · {subjects.length} on record</span>
          <span className="mt-1 block truncate font-data text-[11px] text-zinc-100">
            {current
              ? `${nameOf(current)} · C ${current.predictive_confidence}% · ${current.coverage.filled}/${current.coverage.total ?? 46}`
              : subjects.length
                ? "Most recent engagement"
                : "No engagements yet"}
          </span>
        </span>
        <ChevronDown
          size={13}
          strokeWidth={1.6}
          className={`shrink-0 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          data-testid="subject-picker-menu"
          className="panel absolute right-0 z-40 mt-1 w-[22rem] max-w-[90vw] p-2"
        >
          <div className="flex items-center gap-2 border-b border-[var(--line)] px-2 pb-2">
            <Search size={12} strokeWidth={1.7} className="text-zinc-600" />
            <input
              data-testid="subject-picker-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="search name or id"
              className="w-full bg-transparent py-1 font-data text-[11px] text-zinc-100 outline-none placeholder:text-zinc-700"
            />
          </div>
          <ul className="thin-scroll mt-1 max-h-[18rem] overflow-y-auto">
            {list.map((s) => (
              <li key={s.subject}>
                <button
                  data-testid={`admin-subject-${s.subject}`}
                  onClick={() => {
                    onPick(s.subject);
                    setOpen(false);
                    setQ("");
                  }}
                  className={`flex w-full items-baseline justify-between gap-3 px-2 py-2.5 text-left font-data text-[11px] transition-colors ${
                    subject === s.subject
                      ? "bg-[rgba(255,255,255,0.06)] text-zinc-100"
                      : "text-zinc-400 hover:bg-[rgba(255,255,255,0.03)] hover:text-zinc-100"
                  }`}
                >
                  <span className="truncate">{nameOf(s)}</span>
                  <span className="shrink-0 text-[10px] text-zinc-600">
                    C {s.predictive_confidence}% · {s.coverage.filled}/{s.coverage.total ?? 46}
                    {s.gate_open ? " · OPEN" : ""}
                  </span>
                </button>
              </li>
            ))}
            {list.length === 0 && (
              <li className="px-2 py-3 font-data text-[11px] text-zinc-600">
                {subjects.length ? "No match." : "No engagements yet."}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SubjectPicker;
